import { AnyAri } from '@atlassian/ari';
import { CanvasBoardAri } from '@atlassian/ari/canvas/board';
import { ConfluenceSpaceAri } from '@atlassian/ari/confluence/space';

import { type RawFrameToken } from '@canvas/frame-api-types';
import { unlockAllPaywallsResponseMock } from '@canvas/paywall-test-utils';
import { PaywallFeature } from '@canvas/paywall-types';
import { sleep } from '@canvas/sleep';

import { fetchBoards } from './fetchers.ts';
import { TokenCache, type TokenCacheKey } from './token-cache.ts';

const MAX_RETRY_ATTEMPTS = 5;
const INITIAL_RETRY_DELAY_MS = 100;
const MAX_RETRY_DELAY_MS = 5000;

/**
 * Builds the correct document ARI based on the current URL pathname.
 * Slides use `ari:cloud:canvas:<siteId>:slide/<id>` while whiteboards use
 * `ari:cloud:canvas:<siteId>:board/<id>` — the ARI resource type must match
 * the type used by the server when issuing JWT claims, otherwise the client
 * won't find its claims and will be treated as read-only.
 */
function buildDocumentAri(siteId: string, boardId: string): AnyAri | CanvasBoardAri {
  const isSlide =
    typeof window !== 'undefined' && window.location.pathname.includes('/slide/');

  if (isSlide) {
    return AnyAri.create({
      resourceOwner: 'canvas',
      cloudId: siteId,
      resourceType: 'slide',
      resourceId: boardId,
    });
  }

  return CanvasBoardAri.create({ siteId, boardId });
}

const tokenCache = new TokenCache();

function getRetryDelay(attempt: number): number {
  const exponentialDelay = INITIAL_RETRY_DELAY_MS * Math.pow(2, attempt - 1);
  const jitter = Math.random() * INITIAL_RETRY_DELAY_MS;
  return Math.min(exponentialDelay + jitter, MAX_RETRY_DELAY_MS);
}

function isRetryable401(response: Response): boolean {
  return response.status === 401;
}

async function fetchWithRetry(
  fetchFn: () => Promise<Response>,
  maxAttempts: number = MAX_RETRY_ATTEMPTS,
): Promise<Response> {
  let lastError: Error | undefined;
  let lastResponse: Response | undefined;

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const response = await fetchFn();

      if (response.ok || !isRetryable401(response)) {
        return response;
      }

      lastResponse = response;

      if (attempt < maxAttempts) {
        const delay = getRetryDelay(attempt);
        await sleep(delay);
      }
    } catch (error) {
      const caughtError = error instanceof Error ? error : new Error(String(error));
      lastError = caughtError;

      if (attempt < maxAttempts) {
        const delay = getRetryDelay(attempt);
        await sleep(delay);
      } else {
        throw caughtError;
      }
    }
  }

  if (lastResponse) {
    return lastResponse;
  }

  throw lastError || new Error('Token acquisition failed after retries');
}

export function getTokenFromLocalServer({
  boardId,
  localSpaceId,
  localAsapToken,
  developmentCloudId,
}: {
  boardId: string;
  localSpaceId: string;
  localAsapToken: string;
  developmentCloudId: string;
}): Promise<RawFrameToken> {
  const containerAri = ConfluenceSpaceAri.create({
    spaceId: localSpaceId,
    siteId: developmentCloudId,
  });
  const boardAri = buildDocumentAri(developmentCloudId, boardId);

  return Promise.resolve({
    container: containerAri.toString(),
    board: boardAri.toString(),
    accessToken: localAsapToken,
    mediaToken: 'media-token',
    mediaBaseUrl: 'media-baseurl',
    mediaCollectionId: 'media-collection',
    features: unlockAllPaywallsResponseMock,
    // We return a token with valid Data Security Policy for local development
    policies: { dataSecurityPolicy: { pageExport: true } },
  });
}

async function fetchTokensViaContentId(
  developmentSpaceId: string,
  developmentCloudId: string,
  contentId: string,
) {
  const origin = new URL(window.location.href).origin;
  const url = new URL(`${origin}/gateway/api/ex/confluence/${developmentCloudId}`);
  url.pathname += `/wiki/rest/internal/1.0/canvas/session/${developmentSpaceId}`;
  url.searchParams.set('contentId', contentId);

  // deepcode ignore Ssrf: Local devloop only
  const response = await fetch(url, {
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
  });
  return response;
}

async function fetchJsonFromSpaceIdAndCloudId(
  developmentSpaceId: string,
  developmentCloudId: string,
  boardId: string,
) {
  const origin = new URL(window.location.href).origin;
  const url = new URL(`${origin}/gateway/api/ex/confluence/${developmentCloudId}`);
  url.pathname += `/wiki/rest/internal/1.0/canvas/session/${developmentSpaceId}`;
  url.searchParams.set('whiteboardId', boardId);

  // deepcode ignore Ssrf: Local devloop only
  const response = await fetch(url, {
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
  });
  return response;
}

/** // TODO: re-instate board lookup backup, as followup PR/when necessary */
// async function findValidBoardWithRetries(
//   developmentSpaceId: string,
//   developmentCloudId: string
// ) {
//   // Attempt
//   let safeBoardId: string | null = null;

//   // TODO-who - drill `conatiner Key`
//   const containerKey = process.env.VITE_CANVAS_EAP_CONTAINER_KEY || null;
//   if (!containerKey) {
//     console.warn(`VITE_CANVAS_EAP_CONTAINER_KEY does not exist?`);
//     return;
//   }
//   // This can be abstracted out to a common board data situation, but
//   // re-fetching it here in case of staleness
//   const boardData = await fetchBoards(containerKey);

//   if (!boardData) {
//     throw new Error('board data is not in the shape we expect');
//   }

//   /** If there is  more than 10 cooked boards, something is very wrong. */
//   const ids = boardData.filter((_, i) => i < 10).map((node) => node.referenceId);

//   let validTokenData = null;
//   for (const boardId of ids) {
//     try {
//       const response = await fetchJsonFromSpaceIdAndCloudId(
//         developmentSpaceId,
//         developmentCloudId,
//         boardId
//       );
//       if (response.status === 200) {
//         const data = await response.json();
//         validTokenData = data;
//         safeBoardId = boardId;
//         break;
//       }
//     } catch (error) {
//       console.error(`Error fetching data for board ID ${boardId}:`, error);
//     }
//   }
//   return {
//     boardId: safeBoardId,
//     validTokenData: validTokenData,
//   };
// }

async function rerouteTokenRequestToContentId(
  developmentSpaceId: string,
  developmentCloudId: string,
  boardId: string,
): Promise<Response> {
  const containerKey = process.env.VITE_CANVAS_EAP_CONTAINER_KEY || null;
  if (!containerKey) {
    throw new Error('VITE_CANVAS_EAP_CONTAINER_KEY does not exist');
  }

  const boardData = await fetchBoards(containerKey);

  if (!boardData) {
    throw new Error('board data is not in the shape we expect');
  }

  /** If there is  more than 10 cooked boards, something is very wrong. */
  const boardForReferenceId = boardData.find((node) => node.referenceId === boardId)?.id;
  if (!boardForReferenceId) {
    throw new Error('no boardForReferenceId found!');
  }

  const response = await fetchTokensViaContentId(
    developmentSpaceId,
    developmentCloudId,
    boardForReferenceId,
  );
  return response;
}

async function acquireTokenFromSite({
  boardId,
  developmentSpaceId,
  developmentCloudId,
}: {
  boardId: string;
  developmentSpaceId: string;
  developmentCloudId: string;
}): Promise<RawFrameToken> {
  const response = await fetchWithRetry(() =>
    fetchJsonFromSpaceIdAndCloudId(developmentSpaceId, developmentCloudId, boardId),
  );

  const backupResponse =
    response.status === 404
      ? await fetchWithRetry(() =>
          rerouteTokenRequestToContentId(developmentSpaceId, developmentCloudId, boardId),
        )
      : undefined;

  const responseToUse = backupResponse ?? response;

  if (!responseToUse.ok) {
    throw new Error(
      `Token acquisition failed with status ${responseToUse.status}: ${responseToUse.statusText}`,
    );
  }

  const json = await responseToUse.json();

  const containerAri = ConfluenceSpaceAri.create({
    spaceId: developmentSpaceId,
    siteId: developmentCloudId,
  });
  const boardAri = buildDocumentAri(developmentCloudId, boardId);

  const features: RawFrameToken['features'] = json.features ?? { whiteboards: {} };

  if (features?.whiteboards) {
    features.whiteboards[PaywallFeature.AI_GENERATE] = { isEntitled: true };
    features.whiteboards[PaywallFeature.AI_CLUSTERING] = { isEntitled: true };
    features.whiteboards[PaywallFeature.AI_TEAMMATE] = { isEntitled: true };
    features.whiteboards[PaywallFeature.AI_SMART_BUTTON] = { isEntitled: true };
    features.whiteboards[PaywallFeature.AI_JIRA_SEARCH] = { isEntitled: true };
  }

  return {
    container: containerAri.toString(),
    board: boardAri.toString(),
    accessToken: json.access.token,
    mediaToken: json.media.token,
    mediaBaseUrl: json.media.baseUrl,
    mediaCollectionId: json.media.contentId,
    features: json.features,
    policies: { dataSecurityPolicy: { pageExport: true } },
  };
}

/**
 * `getTokenFromSite()`:
 *
 * 1. First, try get a token from try it with given boardId.
 *
 * If this fails, we utilise findValidBoardWithRetries to get a safe board,
 *
 * Then suggest to the dev that they click out to that "safe for
 * `/canvas/session`" board
 *
 * Features:
 * - Token caching with lifecycle management
 * - Retry strategy with exponential backoff for 401 errors
 * - Request deduplication for concurrent requests
 */
export async function getTokenFromSite({
  boardId,
  developmentSpaceId,
  developmentCloudId,
}: {
  boardId: string;
  developmentSpaceId: string;
  developmentCloudId: string;
}): Promise<RawFrameToken> {
  const cacheKey: TokenCacheKey = {
    boardId,
    developmentSpaceId,
    developmentCloudId,
  };

  const cachedToken = tokenCache.get(cacheKey);
  if (cachedToken) {
    return cachedToken;
  }

  const failureError = tokenCache.getFailure(cacheKey);
  if (failureError) {
    throw failureError;
  }

  const pendingRequest = tokenCache.getPendingRequest(cacheKey);
  if (pendingRequest) {
    return await pendingRequest;
  }

  const tokenPromise = (async () => {
    try {
      const token = await acquireTokenFromSite({
        boardId,
        developmentSpaceId,
        developmentCloudId,
      });

      tokenCache.set(cacheKey, token);
      tokenCache.clearFailure(cacheKey);

      return token;
    } catch (error) {
      const caughtError = error instanceof Error ? error : new Error(String(error));
      tokenCache.recordFailure(cacheKey, caughtError);
      throw caughtError;
    } finally {
      tokenCache.clearPendingRequest(cacheKey);
    }
  })();

  tokenCache.setPendingRequest(cacheKey, tokenPromise);

  return await tokenPromise;
}
